import axios, { AxiosResponse } from "axios";
import { qClient } from "../../../../index";
import { displayError } from "../../../../shared/services/Notifications/NotificationServices";
import type { UserDataType as DataType } from "../../../../shared/types/EditModal/types";
import {
    getCookie,
    transformUserVisibilityLocale,
} from "../../../../shared/components/UniversalEditModal/utils/common";

axios.defaults.headers.common["X-CSRFToken"] = getCookie("csrftoken") || "";

export default function useUserProfile(username: string) {
    const fetchUserData = () => axios.get(`/users/me/edit/`);
    const fetchStatistics = () => axios.get(`/users/${username}/statistics/`);
    const fetchRegions = () => axios.get("/regions/");

    const patchUserData = async (
        data: Partial<DataType>,
        teams: { name: string; slug: string }[] | undefined,
    ) => {
        const teamEntry = teams?.find((t) => t.name === data.team_slug);
        const team_slug = teamEntry?.slug;
        const request = {
            callsign: data.callsign,
            name: data.name,
            description: data.description,
            region_code: data.region?.ru_code,
            weapon_id: data.weapon_id,
            team_slug: team_slug,
            main_spec_name: data.main_spec_name,
            user_profile_visibility: transformUserVisibilityLocale(
                data.user_profile_visibility_selected!,
            ),
            user_stats_visibility: transformUserVisibilityLocale(
                data.user_stats_visibility_selected!,
            ),
            email: data.email_address,
            experience: data.experience || undefined
        };
        
        const cleaned = Object.fromEntries(
            Object.entries(request).filter(([, v]) => v !== undefined),
        );
        const response: AxiosResponse = await axios.patch(
            `/users/me/edit/`,
            cleaned,
        );

        if (response.status === 200) {
            if (data.name) {
                localStorage.setItem("username", data.name);
            }

            qClient.invalidateQueries({
                queryKey: ["userProfile", username],
            });
            
            if (data.name) {
                qClient.invalidateQueries({
                    queryKey: ["userProfile", data.name, "profile"],
                });
            }

            //проверить - может, можно засунуть все ифы на юзернейм в один блок?
            if (data.name) {
                try {
                    window.location.replace(`/Cabinet/${data.name}`);
                } catch (e) {}
            }
        }
    };

    const uploadUserAvatar = async (file: File): Promise<string> => {
        try {
            const res = await axios.put("/users/me/avatar/", file, {
                headers: { "content-type": file.type },
            });

            const url = res.data?.avatar_link?.original;
            if (!url) {
                throw new Error("avatar_link.original not found");
            }

            qClient.invalidateQueries({
                queryKey: ["userProfile", username, "profile"],
            });

            return url;
        } catch (err: any) {
            const serverMessage =
                err?.response?.data?.detail || "Не удалось загрузить аватар";
            displayError(String(serverMessage));
            throw err;
        }
    };

    const uploadUserTheme = async (file: File): Promise<string> => {
        try {
            const res = await axios.put("/users/me/banner/", file, {
                headers: { "content-type": file.type },
            });

            const url = res.data?.banner_link?.original;
            if (!url) {
                throw new Error("banner_link.original not found");
            }

            qClient.invalidateQueries({
                queryKey: ["userProfile", username, "profile"],
            });

            return url;
        } catch (err: any) {
            const serverMessage =
                err?.response?.data?.detail || "Не удалось загрузить баннер";
            displayError(String(serverMessage));
            throw err;
        }
    };

    const fetchSpecialisations = async () =>
        await axios.get("/specialisations/");

    //может тут вообще не нужны камуфляжи? проверить
    // const fetchCamouflages = async () => await axios.get("/info/camouflages/");

    const fetchArmoryList = async (username: string) =>
        await axios.get(`/users/${username}/weapons/`);

    return {
        fetchUserData,
        fetchStatistics,
        fetchRegions,
        patchUserData,
        uploadUserAvatar,
        uploadUserTheme,
        // fetchCamouflages,
        fetchSpecialisations,
        fetchArmoryList,
    };
}
